import _ from 'lodash';
import React from 'react';
import { Page, Form } from '@janus.team/janus-particles';
import { Redirect } from 'react-router-dom';
import VError from 'verror';

import { proxyFetch } from '../fetch';
import { getGoogleCredentials } from '../oauth';

const requiredField = (value, validation, message) => (!_.trim(value) ? 'Required' : (validation(value) && message));
const validateForm = ({ projectId, projectName }) => ({
  projectId: requiredField(
    projectId,
    (value) => (!/^[a-z]{1}[a-z\d-]{4,28}[a-z\d]$/.test(_.trim(value))),
    'must be 6 to 30 lowercase letters, digits, or hyphens. It must start with a letter. Trailing hyphens are prohibited.',
  ),
  projectName: requiredField(
    projectName,
    (value) => (!/^[a-zA-Z\d-'"!]{1}[a-zA-Z\d-"'\s!]{3,29}$/i.test(_.trim(value))),
    'must be 4 to 30 characters. Allowed characters are: lowercase and uppercase letters, numbers, hyphen, single-quote, double-quote, space, and exclamation point.',
  ),
});
const useFormInput = (initialValue = '') => {
  const [input, setInput] = React.useState({ value: initialValue });
  return [{
    value: input.value,
    touched: input.touched,
    onChange: ({ target: { value } }) => setInput((previous) => ({ ...previous, value })),
    onBlur: () => setInput((previous) => ({ ...previous, touched: true })),
  }];
};

const errorMessageFor = ({ code, message = 'Unknown error' }) => _.get({
  ConflictError: () => 'Project ID already exists',
  UnauthorizedError: () => 'Could not authenticate',
  ValidationError: () => _.replace(message || 'Missing or Invalid form data', '/project/', ''),
  BadRequestError: () => 'Invalid form data',
}, code, () => `Failed to submit form: ${message}`)();

const projectCreationError = (body) => {
  const error = { code: _.get(body, 'error.code'), message: _.get(body, 'error.message') };
  return new VError({
    name: _.get(error, 'code', 'HttpError'),
    info: {
      responseBody: body,
    },
  }, errorMessageFor(error));
};

export const NewProject = ({ match: { params: { rackspaceAccountNumber } }, history }) => {
  const [form, setForm] = React.useState({
    submitting: false, success: false, error: null, data: null,
  });
  const [validation, setValidation] = React.useState({});
  const [projectIdInput] = useFormInput('');
  const [projectNameInput] = useFormInput('');

  React.useEffect(() => {
    setValidation(validateForm({ projectId: projectIdInput.value, projectName: projectNameInput.value }));
  }, [projectIdInput.value, projectNameInput.value]);

  const handleSubmit = (event) => {
    _.invoke(event, 'preventDefault');
    if (_.some(validation)) {
      return;
    }
    const googleCredentials = getGoogleCredentials() || { idToken: '', accessToken: '' };

    const projectId = _.trim(projectIdInput.value);
    const projectName = _.trim(projectNameInput.value);
    const project = {
      projectId, rackspaceAccountNumber, name: projectName, source: 'creation',
    };
    const body = { googleCredentials, project };
    const headers = { 'X-Tenant-Id': rackspaceAccountNumber };
    const submitting = () => setForm({ submitting: true });
    const success = (data) => setForm((previous) => ({
      ...previous, submitting: false, success: true, data,
    }));
    const failure = (error) => {
      if (VError.hasCauseWithName(error, 'BadRequestError')) {
        const errInfo = VError.info(error);
        const originalErrorMessage = _.get(errInfo, ['responseBody', 'error', 'message']);
        const googleValidationRe = /field \[(.*)\] has issue \[(.*)\]/;
        const googleValidationError = googleValidationRe.exec(originalErrorMessage);
        if (googleValidationError) {
          setValidation({
            [_.camelCase(googleValidationError[1])]: googleValidationError[2],
          });
        }
      }
      setForm((previous) => ({
        ...previous, submitting: false, success: false, error,
      }));
    };
    const handleResponse = (res) => (res.ok ? res.parseJson()
      : res.parseJson().then(projectCreationError).then((error) => Promise.reject(error)));
    submitting();
    proxyFetch(`${process.env.REACT_APP_GCP_API}/projects`, { headers, body, method: 'POST' }).then(handleResponse).then(success, failure);
  };

  if (form.success) {
    const projectId = _.trim(projectIdInput.value);
    return (<Redirect to={`/racker/customers/${rackspaceAccountNumber}/projects?project=${projectId}`} />);
  }

  return (
    <>
      <Page.MainHeader title="GCP Projects" />
      <Page.MainBody>
        <Page.MainBodySection title="Create Managed GCP Project">
          <p>
            Create a new Managed GCP project and add it to the customer
            {'\''}
            s account.
          </p>
          <Form onSubmit={handleSubmit}>
            <Form.Input id="rackspaceAccountNumber" name="rackspaceAccountNumber" type="hidden" value={rackspaceAccountNumber} />
            <Form.Input
              {...projectIdInput}
              error={validation.projectId}
              id="projectId"
              name="projectId"
              label="Project ID"
              placeholder="Enter globally unique Project ID"
              help="Should be globally unique across all GCP projects"
              required
            />
            <Form.Input
              {...projectNameInput}
              error={validation.projectName}
              name="projectName"
              label="Project Name"
              placeholder="Enter a name that will clearly identify this project in the console"
              help="Should be identifiable in the GCP Console and APIs"
              required
            />
            {form.error && (<Form.Error>{form.error.message}</Form.Error>)}
            <Form.Actions dirty valid={!_.some(validation)} submitting={!!form.submitting}>
              <Form.Action type="submit" requiresChanges requiresValid>
                Create
              </Form.Action>
              <Form.Action type="button" tertiary onClick={history.goBack}>
                Cancel
              </Form.Action>
            </Form.Actions>
          </Form>
        </Page.MainBodySection>
      </Page.MainBody>
    </>
  );
};
